/**
 * Capture an HTML element with html2canvas and download as a multi-page PDF via jspdf.
 * Used for the Student Analytics Report.
 */

import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;

export interface PdfExportOptions {
  /** Filename without extension */
  filename?: string;
  /** Scale factor for canvas (higher = sharper, larger file). Default 2 */
  scale?: number;
  /** Delay in ms before capture (e.g. for fonts/charts to paint). Default 300 */
  delayBeforeCapture?: number;
}

/**
 * Render the given element to canvas, then build a PDF (splitting across pages if needed) and trigger download.
 */
export async function exportElementToPdf(
  element: HTMLElement,
  options: PdfExportOptions = {}
): Promise<void> {
  const { filename = 'student-analytics-report', scale = 2, delayBeforeCapture = 300 } = options;

  await new Promise((r) => setTimeout(r, delayBeforeCapture));

  const canvas = await html2canvas(element, {
    scale,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
  });

  const imgWidth = A4_WIDTH_MM;
  const pageHeight = A4_HEIGHT_MM;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  let heightLeft = imgHeight;

  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF('p', 'mm', 'a4');

  let position = 0;
  pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;

  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }

  const safeName = `${filename}-${new Date().toISOString().slice(0, 10)}.pdf`;
  pdf.save(safeName);
}
